// Public privacy policy required by GDPR (Reglamento UE 2016/679) and the
// Spanish LOPDGDD 3/2018. This is a sensible starting template for a
// SaaS B2B operating from Spain with EU sub-processors. The user should
// review it with legal counsel before first paying customer — Formular is
// pre-revenue so the bar is "informed processing" rather than "fully
// audited contract".
//
// Versioned via the field name fiscalConsent.version in Firestore. Bump
// the in-page version string AND the Firestore consent default whenever
// material processing changes (new sub-processor, new purpose, etc.).

export function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-bg">
      <div className="max-w-2xl mx-auto px-6 py-16">
        <h1 className="text-3xl font-black text-ink mb-2">
          Política de Privacidad
        </h1>
        <p className="text-sm text-ink-3 mb-10">
          Versión <strong>v1</strong> · Última actualización: 2026-04-27
        </p>

        <Section title="1. Responsable del tratamiento">
          Formular (en adelante, &laquo;Formular&raquo; o &laquo;nosotros&raquo;)
          actúa como responsable del tratamiento de los datos personales
          que el Cliente facilita a través de esta plataforma. Para
          cualquier consulta sobre privacidad puedes escribir a{" "}
          <a
            href="mailto:hello@formular.so"
            className="underline text-ink-2 hover:text-ink"
          >
            hello@formular.so
          </a>
          .
        </Section>

        <Section title="2. Datos que tratamos">
          Tratamos las siguientes categorías de datos:
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>
              <strong>Identificación de la cuenta</strong>: nombre, email
              de Google, organización a la que perteneces.
            </li>
            <li>
              <strong>Datos fiscales de la organización</strong>: razón
              social, NIF / CIF / VAT, dirección fiscal, país, email de
              facturación. Se solicitan únicamente al activar la
              facturación.
            </li>
            <li>
              <strong>Datos de facturación y pago</strong>: gestionados
              por Stripe Payments Europe, Ltd. Formular <em>no almacena</em>{" "}
              números de tarjeta ni IBANs; solo recibe la referencia del
              cliente y el estado de los pagos.
            </li>
            <li>
              <strong>Datos de uso del servicio</strong>: tokens
              consumidos, peticiones, almacenamiento utilizado y registros
              de auditoría asociados a tu cuenta.
            </li>
          </ul>
        </Section>

        <Section title="3. Finalidades y base jurídica">
          Tratamos los datos para:
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>
              <strong>Ejecución del contrato</strong> (art. 6.1.b RGPD):
              prestar el servicio contratado, gestionar tu suscripción,
              emitir facturas y dar soporte.
            </li>
            <li>
              <strong>Cumplimiento de obligaciones legales</strong> (art.
              6.1.c RGPD): conservación de facturas y datos contables
              durante los plazos exigidos por la normativa fiscal y
              mercantil española (mínimo 6 años).
            </li>
            <li>
              <strong>Interés legítimo</strong> (art. 6.1.f RGPD):
              seguridad de la plataforma, prevención de fraude y registros
              de auditoría imprescindibles.
            </li>
          </ul>
        </Section>

        <Section title="4. Encargados y subcontratistas (sub-procesadores)">
          Para prestar el servicio compartimos datos estrictamente
          necesarios con los siguientes encargados de tratamiento:
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>
              <strong>Stripe Payments Europe, Ltd.</strong> (Irlanda) —
              procesador de pagos, gestión de suscripciones y emisión de
              facturas. Sus garantías y certificaciones están en{" "}
              <a
                href="https://stripe.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-ink-2 hover:text-ink"
              >
                stripe.com/privacy
              </a>
              .
            </li>
            <li>
              <strong>Google LLC / Google Cloud EMEA Ltd.</strong>{" "}
              (Irlanda) — alojamiento de la aplicación (Firebase Hosting,
              Cloud Functions y Firestore en región europe-west1). Datos
              en reposo dentro del Espacio Económico Europeo.
            </li>
            <li>
              <strong>Resend, Inc.</strong> — entrega de correos
              transaccionales (invitaciones, avisos de cambio de precio).
              Transferencia internacional cubierta por las Cláusulas
              Contractuales Tipo aprobadas por la Comisión Europea.
            </li>
          </ul>
          <p className="mt-2">
            No vendemos ni cedemos datos personales con fines comerciales.
          </p>
        </Section>

        <Section title="5. Plazo de conservación">
          Conservamos los datos mientras tu cuenta esté activa y, una vez
          dada de baja, durante los plazos legales de prescripción y
          conservación documental (6 años para datos contables, 4 años
          para datos fiscales). Pasados esos plazos, los datos se eliminan
          o anonimizan.
        </Section>

        <Section title="6. Tus derechos">
          Puedes ejercer en cualquier momento los derechos de acceso,
          rectificación, supresión, oposición, limitación del tratamiento
          y portabilidad escribiendo a{" "}
          <a
            href="mailto:hello@formular.so"
            className="underline text-ink-2 hover:text-ink"
          >
            hello@formular.so
          </a>
          . Si consideras que el tratamiento no se ajusta a la normativa,
          puedes presentar una reclamación ante la Agencia Española de
          Protección de Datos (
          <a
            href="https://www.aepd.es"
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-ink-2 hover:text-ink"
          >
            aepd.es
          </a>
          ).
        </Section>

        <Section title="7. Seguridad">
          Aplicamos medidas técnicas y organizativas razonables para
          proteger los datos (cifrado en tránsito, control de acceso por
          roles, registros de auditoría inmutables y revisión periódica
          de sub-procesadores). Ningún sistema online es 100 % seguro:
          comunicaremos cualquier brecha relevante conforme al art. 33
          RGPD.
        </Section>

        <Section title="8. Cambios en esta política">
          Podemos actualizar esta política para reflejar cambios legales o
          de subcontratistas. La versión vigente queda identificada por
          su número (v1, v2, etc.) y por la fecha de la cabecera. Si los
          cambios afectan al consentimiento que aceptaste, te
          solicitaremos uno nuevo antes de continuar.
        </Section>

        <p className="text-xs text-ink-3 mt-12">
          Para cualquier consulta puedes escribir a{" "}
          <a
            href="mailto:hello@formular.so"
            className="underline text-ink-2 hover:text-ink"
          >
            hello@formular.so
          </a>
          .
        </p>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-8">
      <h2 className="text-base font-black text-ink mb-2">{title}</h2>
      <div className="text-sm leading-relaxed text-ink-2">{children}</div>
    </section>
  );
}
